import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faBuilding } from "@fortawesome/pro-solid-svg-icons/faBuilding";
import { faCalendarAlt } from "@fortawesome/pro-solid-svg-icons/faCalendarAlt";
import { faHandshake } from "@fortawesome/pro-solid-svg-icons/faHandshake";
import { faMoneyBillWave } from "@fortawesome/pro-solid-svg-icons/faMoneyBillWave";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Steps from "antd/es/steps";
import { differenceInBusinessDays, differenceInCalendarDays } from "date-fns/esm";
import numeral from "numeral";
import { FormattedMessage, useIntl } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import Role from "@mapmycustomers/shared/enum/Role";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";

import TextWithLine from "@app/component/typography/TextWithLine";
import localSettings from "@app/config/LocalSettings";
import ReportType from "@app/enum/ReportType";
import StandardHeader from "@app/scene/reports/components/Header/StandardHeader";
import EngagementCard from "@app/scene/reports/components/Overview/components/Engagement";
import RecentCompaniesCard from "@app/scene/reports/components/Overview/components/RecentCompanies";
import RecordSource from "@app/scene/reports/components/Overview/components/Source";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { money } from "@app/util/formatters";

import { AvgLevel } from "../../enums/AvgLevel";
import MapFilter from "../../enums/MapFilter";
import { getFeaturePathByMapFilter, reportsOverviewAnalytics } from "../../reportsAnalytics";
import { fetchOverviewCardData, fetchOverviewMapDeals } from "../../store/actions";
import OverviewData, { OverviewMapData } from "../../store/OverviewData";
import { getDateRange, getOverviewData, getOverviewMapData } from "../../store/selectors";
import DateRange from "../../types/DateRange";
import useParentUserIds from "../../utils/useParentUserIds";
import useUserIds from "../../utils/useUserIds";
import BaseScreenProps from "../BaseScreenProps";
import ChartCard from "../charts/ChartCard";
import ReportMap from "../Map/ReportMap";
import OverviewMapFilter from "../MapFilters/OverviewMapFilter";
import MetricCard from "../MetricCard";

import ActivityByTypeChart from "./components/ActivityByTypeChart";
import DealsPerStageChart from "./components/DealsPerStageChart";
import DealsWonOverTimeChart from "./components/DealsWonOverTimeChart";
import RecordsOverTimeChart from "./components/RecordsOverTimeChart";
import { NOT_SELECTED_VALUE, RevenueExtraFilter } from "./components/RevenueExtraFilter";
import RevenueWonOverTimeChart from "./components/RevenueWonOverTimeChart";
import TimeOffsetAlert from "./components/TimeOffsetAlert";
import GrossMetrics from "./GrossMetrics";
import { messages } from "./messages";
import styles from "./Overview.module.scss";

const avgFormat = (value: number) => numeral(value).format("0.0");

interface Props extends BaseScreenProps {
  dateRange: DateRange;
  fetchOverviewData: typeof fetchOverviewCardData.request;
  fetchOverviewMapDeals: typeof fetchOverviewMapDeals.request;
  overviewData: OverviewData;
  overviewMapData: OverviewMapData;
  teams: Team[];
  users: User[];
}

const Overview: React.FC<Props> = ({
  dateRange,
  fetchOverviewData,
  fetchOverviewMapDeals,
  overviewData,
  overviewMapData,
  selectedTeams,
  selectedUsers,
  teams,
  users,
}) => {
  const intl = useIntl();
  const teamsForSelectedUsers = useMemo(() => {
    return Array.from(new Set(selectedUsers.flatMap(({ teams }) => teams.map(({ id }) => id))));
  }, [selectedUsers]);

  const avgLevel = useMemo<AvgLevel | undefined>(() => {
    if (selectedTeams && selectedTeams.length > 0 && selectedUsers.length !== users.length) {
      return AvgLevel.ORG;
    }
    if (
      teamsForSelectedUsers.length === 1 &&
      selectedUsers.every((user) => user.teams.length > 0 && user.role.key !== Role.OWNER)
    ) {
      return AvgLevel.TEAM;
    }
    return undefined;
  }, [selectedTeams, selectedUsers, teamsForSelectedUsers, users]);

  // This could be safely used for division because it > 0
  const similarLength = useMemo<number>(() => {
    if (avgLevel === AvgLevel.ORG) {
      return teams.length || 1;
    }
    if (avgLevel === AvgLevel.TEAM) {
      return (
        teams.find(({ users }) => (users as User[]).find((user) => selectedUsers.includes(user)))
          ?.users.length || 1
      );
    }
    return 1;
  }, [avgLevel, selectedUsers, teams]);

  const parentUserIds: Array<User["id"]> = useParentUserIds(
    teams,
    users,
    selectedUsers,
    selectedTeams,
    teamsForSelectedUsers
  );

  const userIds: Array<User["id"]> = useUserIds(selectedUsers);
  const [mapFilter, setMapFilter] = useState<MapFilter>(MapFilter.CLOSED_WON_REVENUE);

  const [selectedRevenueFunnelId, setSelectedRevenueFunnelId] =
    useState<Funnel["id"]>(NOT_SELECTED_VALUE);
  const [selectedRevenueStageId, setSelectedRevenueStageId] =
    useState<Stage["id"]>(NOT_SELECTED_VALUE);

  useChangeTracking(() => {
    reportsOverviewAnalytics.clicked(getFeaturePathByMapFilter(mapFilter));
  }, [mapFilter]);

  const startTime = dateRange.startDate.getTime();
  const endTime = dateRange.endDate.getTime();
  const numberOfDays = differenceInCalendarDays(dateRange.endDate, dateRange.startDate) + 1;

  useEffect(
    () => {
      fetchOverviewData({
        dateRange,
        parentUserIds,
        userIds,
      });
    },
    // disabled deps linting coz it fails to understand a complex expression with getTime()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startTime, endTime, fetchOverviewData, userIds]
  );

  useEffect(() => {
    fetchOverviewMapDeals({
      filter: mapFilter,
      funnelId: selectedRevenueFunnelId,
      stageId: selectedRevenueStageId,
      userIds,
    });
  }, [fetchOverviewMapDeals, mapFilter, selectedRevenueFunnelId, selectedRevenueStageId, userIds]);

  const {
    activityTypesWithCount,
    dealsPerFunnel,
    mapFunnelStages,
    maxAmountByStageType,
    numDealsLost,
    numDealsWon,
    parentNumDealsLost,
    parentNumDealsWon,
    parentRevenueLost,
    parentRevenueWon,
    parentStatistics,
    recordsOverTime,
    revenueLost,
    revenueWon,
    statistics,
  } = overviewData;

  const { deals, loading } = overviewMapData;

  const funnels: Funnel[] = useMemo(
    () => Object.values(mapFunnelStages).map(({ stages, ...funnel }) => funnel as Funnel),
    [mapFunnelStages]
  );

  const maxDealSize =
    mapFilter === MapFilter.CLOSED_WON_REVENUE
      ? maxAmountByStageType[DealStageType.WON] ?? 0
      : mapFilter === MapFilter.CLOSED_LOST_REVENUE
      ? maxAmountByStageType[DealStageType.LOST] ?? 0
      : maxAmountByStageType[DealStageType.DEFAULT] ?? 0;

  const avgDealSizeWon = numDealsWon > 0 ? revenueWon / numDealsWon : 0;
  const parentAvgDealSizeWon = parentNumDealsWon > 0 ? parentRevenueWon / parentNumDealsWon : 0;
  const avgDealSizeLost = numDealsLost > 0 ? revenueLost / numDealsLost : 0;
  const parentAvgDealSizeLost = parentNumDealsLost > 0 ? parentRevenueLost / parentNumDealsLost : 0;
  const halfMaxDealSize = maxDealSize / 2;

  const dateRangeDays = differenceInBusinessDays(dateRange.endDate, dateRange.startDate) + 1;

  const avgActivitiesPerWeekday = (statistics.activitiesCompleted ?? 0) / dateRangeDays;
  const avgDealsPerWeekday = (statistics.dealsCreated ?? 0) / dateRangeDays;
  const avgCompaniesPerWeekday = (statistics.accountsCreated ?? 0) / dateRangeDays;
  const avgPeoplePerWeekday = (statistics.contactsCreated ?? 0) / dateRangeDays;
  const avgRoutesPerWeekday = (statistics.routesCreated ?? 0) / dateRangeDays;

  const similarAvgActivitiesPerWeekday =
    (parentStatistics.activitiesCompleted ?? 0) / similarLength / dateRangeDays;
  const similarAvgDealsPerWeekday =
    (parentStatistics.dealsCreated ?? 0) / similarLength / dateRangeDays;
  const similarAvgCompaniesPerWeekday =
    (parentStatistics.accountsCreated ?? 0) / similarLength / dateRangeDays;
  const similarAvgPeoplePerWeekday =
    (parentStatistics.contactsCreated ?? 0) / similarLength / dateRangeDays;
  const similarAvgRoutesPerWeekday =
    (parentStatistics.routesCreated ?? 0) / similarLength / dateRangeDays;

  const pins = useMemo(
    () =>
      deals
        // Strictly speaking, this shouldn't be required because we got deals with mapped === true filter
        // But in reality we once faced Platform issue, so let's have this filter
        // https://mapmycustomers.slack.com/archives/CMRDJAMV2/p1678999933793009?thread_ts=1678997211.438839&cid=CMRDJAMV2
        .filter(({ account, contact }) => account?.geoPoint ?? contact?.geoPoint)
        .map(({ id, account, amount, contact }) => ({
          id,
          amount,
          // One of related entity will have geoPoint, because we got deals with mapped === true filter
          geoPoint: account?.geoPoint ?? contact?.geoPoint,
          marker: "map-marker",
          objType: ReportType.REVENUE,
        })),
    [deals]
  );

  return (
    <div className={styles.container}>
      {/* ============== DATE RANGE ============== */}
      <StandardHeader reportType={ReportType.OVERVIEW} />
      <TimeOffsetAlert className={styles.timeOffsetAlert} />
      <TextWithLine>
        <FormattedMessage
          id="reports.overview.grossMetrics"
          defaultMessage="Gross Metrics"
          description="Title for Gross Metric section on Overview Screen."
        />
      </TextWithLine>
      {/* ============== GROSS METRICS ============== */}
      <GrossMetrics
        avgLevel={avgLevel}
        overviewData={overviewData}
        selectedTeams={selectedTeams}
        selectedUsers={selectedUsers}
        similarLength={similarLength}
      />

      <br />

      <TextWithLine>
        <FormattedMessage
          id="reports.overview.averageMetrics"
          defaultMessage="Average Metrics"
          description="Title for Average Metric section on Overview Screen ."
        />
      </TextWithLine>
      {/* ============== AVERAGE METRICS ============== */}
      <div className={styles.metricSection}>
        <MetricCard
          alignment="left"
          average={
            avgLevel !== undefined &&
            intl.formatMessage(messages[avgLevel], {
              average: intl.formatNumber(parentAvgDealSizeWon, {
                currency: localSettings.getCurrencyCode(),
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                style: "currency",
              }),
            })
          }
          icon={faMoneyBillWave}
          title={intl.formatMessage({
            id: "reports.overview.averageMetric.dealSizeWon",
            defaultMessage: "Avg. Deal Size Won",
            description: "Card title for Avg. Deal Size Won on Overview Screen.",
          })}
          type="success"
          value={intl.formatNumber(avgDealSizeWon, {
            currency: localSettings.getCurrencyCode(),
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: "currency",
          })}
        />
        <MetricCard
          alignment="left"
          average={
            avgLevel !== undefined &&
            intl.formatMessage(messages[avgLevel], {
              average: intl.formatNumber(parentAvgDealSizeLost, {
                currency: localSettings.getCurrencyCode(),
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                style: "currency",
              }),
            })
          }
          icon={faMoneyBillWave}
          title={intl.formatMessage({
            id: "reports.overview.averageMetric.dealSizeLost",
            defaultMessage: "Avg. Deal Size Lost",
            description: "Card title for Avg. Deal Size Lost on Overview Screen.",
          })}
          type="error"
          value={intl.formatNumber(avgDealSizeLost, {
            currency: localSettings.getCurrencyCode(),
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: "currency",
          })}
        />
      </div>
      <div className={styles.metricSection}>
        <MetricCard
          average={
            avgLevel !== undefined &&
            intl.formatMessage(messages[avgLevel], {
              average: avgFormat(similarAvgActivitiesPerWeekday),
            })
          }
          icon={faCalendarAlt}
          title={intl.formatMessage({
            id: "reports.overview.averageMetric.activitiesPerWeekday",
            defaultMessage: "Avg. Activities / Weekday",
            description: "Card title for Avg. Activities / Weekday on Overview Screen.",
          })}
          value={avgFormat(avgActivitiesPerWeekday)}
        />

        <MetricCard
          average={
            avgLevel !== undefined &&
            intl.formatMessage(messages[avgLevel], {
              average: avgFormat(similarAvgDealsPerWeekday),
            })
          }
          icon={faHandshake}
          title={intl.formatMessage({
            id: "reports.overview.averageMetric.dealsPerWeekday",
            defaultMessage: "Avg. Deals / Weekday",
            description: "Card title for Avg. Deals / Weekday on Overview Screen.",
          })}
          value={avgFormat(avgDealsPerWeekday)}
        />

        <MetricCard
          average={
            avgLevel !== undefined &&
            intl.formatMessage(messages[avgLevel], {
              average: avgFormat(similarAvgCompaniesPerWeekday),
            })
          }
          icon={faBuilding}
          title={intl.formatMessage({
            id: "reports.overview.averageMetric.companiesPerWeekday",
            defaultMessage: "Avg. Companies / Weekday",
            description: "Card title for Avg. Companies / Weekday on Overview Screen.",
          })}
          value={avgFormat(avgCompaniesPerWeekday)}
        />

        <MetricCard
          average={
            avgLevel !== undefined &&
            intl.formatMessage(messages[avgLevel], {
              average: avgFormat(similarAvgPeoplePerWeekday),
            })
          }
          icon={faUser}
          title={intl.formatMessage({
            id: "reports.overview.averageMetric.peoplePerWeekday",
            defaultMessage: "Avg. People / Weekday",
            description: "Card title for Avg. People / Weekday on Overview Screen.",
          })}
          value={avgFormat(avgPeoplePerWeekday)}
        />
        <MetricCard
          average={
            avgLevel !== undefined &&
            intl.formatMessage(messages[avgLevel], {
              average: avgFormat(similarAvgRoutesPerWeekday),
            })
          }
          icon={faUser}
          title={intl.formatMessage({
            id: "reports.overview.averageMetric.routesPerWeekday",
            defaultMessage: "Avg. Routes / Weekday",
            description: "Card title for Avg. Routes / Day on Overview Screen.",
          })}
          value={avgFormat(avgRoutesPerWeekday)}
        />
      </div>

      <br />

      {/* ============== CHARTS ============== */}
      <TextWithLine>
        <FormattedMessage
          id="reports.overview.charts"
          defaultMessage="Charts"
          description="Title for Charts section on Overview Screen ."
        />
      </TextWithLine>
      <br />
      <Row gutter={[32, 32]}>
        <Col span={10}>
          <ChartCard>
            <DealsPerStageChart data={dealsPerFunnel} />
          </ChartCard>
        </Col>
        <Col span={14}>
          <ChartCard>
            <DealsWonOverTimeChart />
          </ChartCard>
        </Col>
        <Col span={14}>
          <ChartCard>
            <RevenueWonOverTimeChart />
          </ChartCard>
        </Col>
        <Col span={10}>
          <ChartCard>
            <ActivityByTypeChart activityTypesWithCount={activityTypesWithCount} />
          </ChartCard>
        </Col>
        <Col span={24}>
          <ChartCard>
            <RecordsOverTimeChart numberOfDays={numberOfDays} recordsOverTime={recordsOverTime} />
          </ChartCard>
        </Col>
        <Col span={24}>
          <EngagementCard />
        </Col>
        <Col span={24}>
          <RecentCompaniesCard selectedUserIds={userIds} />
        </Col>
        <Col span={12}>
          <RecordSource selectedUsers={selectedUsers} />
        </Col>
      </Row>

      <br />
      <br />

      {/* ============== REVENUE HEAT MAPS ============== */}
      <Row>
        <Col span={16}>
          <ReportMap mapLoading={loading} pins={pins} showingHeatMap={true} />
          <div className={styles.revenueLegend} />
          <Steps className={styles.revenueLegendSteps} progressDot status="wait">
            <Steps.Step description="$0" />
            <Steps.Step description={`${money(halfMaxDealSize)}`} />
            <Steps.Step description={`${money(maxDealSize)}`} />
          </Steps>
        </Col>
        <Col className={styles.revenueHeatMapFiltersContainer} span={6}>
          <OverviewMapFilter filter={mapFilter} onChange={setMapFilter} />
          {mapFilter === MapFilter.GROSS_OPEN_REVENUE && (
            <RevenueExtraFilter
              funnels={funnels}
              onFunnelSelect={setSelectedRevenueFunnelId}
              onStageSelect={setSelectedRevenueStageId}
              selectedFunnel={selectedRevenueFunnelId}
              selectedStage={selectedRevenueStageId}
              stages={mapFunnelStages[selectedRevenueFunnelId]?.stages ?? []}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  dateRange: getDateRange(state),
  overviewData: getOverviewData(state),
  overviewMapData: getOverviewMapData(state),
  teams: getTeams(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  fetchOverviewData: fetchOverviewCardData.request,
  fetchOverviewMapDeals: fetchOverviewMapDeals.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Overview);
